import React from "react";
import { Button } from "@src/ui/button";
import { Dialog, DialogContent } from "@src/ui/dialog";
import LanguageIcon from "@src/ui/icons/18px/language";
import PenOutlineIcon from "@src/ui/icons/18px/pen-outline";
import Phone2Icon from "@src/ui/icons/18px/phone-2";
import { IconProps } from "@src/ui/icons/types";
import Text from "@src/ui/text";
import OpusDocumentIllustration from "@src/ui/illustrations/OpusDocumentIllustration";
import { cn } from "@src/ui/lib/utils";
import PdfDocumentIllustration from "@src/ui/illustrations/PdfDocumentIllustration";
import { gql } from "@apollo/client";
import { useConvertPdfToOpusDocDialog_CreateTrainingResourceMutation } from "@src/components/shared/ConvertPdfToOpusDocDialog.generated";
import { useRouter } from "next/router";
import { addErrorToast } from "@src/utils/toast";
import { useFileListUpload } from "@src/hooks/useFileListUpload";
import { MediaFragment as FileListUploadMedia } from "@src/hooks/useFileUpload";
import { UploadedTrainingResourceFragment } from "@src/components/modals/bulkUploadTrainingResourceModal/BulkUploadTrainingResourceModal.generated";
import Spinner from "@src/deprecatedDesignSystem/components/Spinner";
import {
  CreateTrainingResourceInput,
  TrainingResourceVersionType,
} from "@src/types.generated";

type CardOption = "opus-doc" | "pdf";

interface Props {
  pdfFile: File;
  onClose: () => void;
  onTrainingResourceCreated?: (
    trainingResource: UploadedTrainingResourceFragment,
  ) => void;
}

const ConvertPdfToOpusDocDialog: React.FC<Props> = (props) => {
  const router = useRouter();

  const [selectedCardOption, setSelectedCardOption] =
    React.useState<CardOption>("opus-doc");
  const [media, setMedia] = React.useState<FileListUploadMedia | null>(null);

  const [uploadPdfFile, { loading: fileListLoading }] = useFileListUpload(
    (media: FileListUploadMedia[]) => {
      if (media.length === 0) {
        // Message is intentional.
        // User is not aware of any "upload".
        // User has just selected a PDF file.
        addErrorToast({
          message: "Unable to select file. Please try again.",
          callsite: "ConvertPdfToOpusDocDialog",
        });
        return;
      }
      setMedia(media[0]);
    },
  );

  const [createTrainingResource, { loading: createTrainingResourceLoading }] =
    useConvertPdfToOpusDocDialog_CreateTrainingResourceMutation({
      onCompleted(data) {
        if (
          data.createTrainingResourceV2.success &&
          data.createTrainingResourceV2.trainingResource
        ) {
          if (props.onTrainingResourceCreated) {
            props.onTrainingResourceCreated(
              data.createTrainingResourceV2.trainingResource,
            );
            props.onClose();
            return;
          }

          router.push({
            pathname: "/library/library-item/[id]",
            query: {
              id: data.createTrainingResourceV2.trainingResource.libraryItem
                ?.id,
            },
          });
          props.onClose();
        } else {
          addErrorToast({
            message: "Unable to convert PDF. Please try again.",
            callsite: "ConvertPdfToOpusDocDialog",
          });
        }
      },
    });

  const handleOnSave = React.useCallback(async () => {
    const input: CreateTrainingResourceInput =
      selectedCardOption === "opus-doc"
        ? {
            name: {
              en: props.pdfFile?.name || "",
            },
            mediaInput: {
              mediaUrls: {
                en: media?.url || "",
              },
            },
            type: TrainingResourceVersionType.RichTextDocument,
          }
        : {
            name: {
              en: props.pdfFile?.name || "",
            },
            mediaInput: {
              mediaUrls: {
                en: media?.url || "",
              },
            },
            type: TrainingResourceVersionType.Media,
          };
    const res = await createTrainingResource({
      variables: {
        input,
      },
    });

    if (
      res.data?.createTrainingResourceV2.success &&
      res.data?.createTrainingResourceV2.trainingResource?.libraryItem?.id
    ) {
      router.push({
        pathname: "/library/library-item/[id]",
        query: {
          id: res.data?.createTrainingResourceV2.trainingResource?.libraryItem
            ?.id,
        },
      });
    }
    props.onClose();
  }, [createTrainingResource, media?.url, props, router, selectedCardOption]);

  const saveButtonText = React.useMemo(() => {
    return selectedCardOption === "opus-doc"
      ? "Convert to Opus Doc"
      : "Upload and Preview";
  }, [selectedCardOption]);

  React.useEffect(() => {
    uploadPdfFile([props.pdfFile]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog open onOpenChange={props.onClose}>
      <DialogContent className="min-w-[720px] gap-0 p-0">
        {(fileListLoading || createTrainingResourceLoading) && (
          <div className="bg-background/80 absolute inset-0 z-10 flex flex-col items-center justify-center backdrop-blur-sm">
            <Spinner size={48} centered />
            <Text
              type="P2"
              fontWeight="Medium"
              className="mt-4 text-muted-foreground"
            >
              {fileListLoading
                ? "Uploading document…"
                : createTrainingResourceLoading
                  ? "Creating resource…"
                  : "Converting document…"}
            </Text>
          </div>
        )}
        <div className="flex flex-col items-center justify-center border-b border-input p-4">
          <Text type="P2" fontWeight="SemiBold" multiline>
            Do you want to convert this file into an Opus Document?
          </Text>
        </div>
        <div className="flex flex-1 items-center justify-center gap-3 self-stretch p-3">
          <OpusDocCardOption
            selected={selectedCardOption === "opus-doc"}
            onClick={() => setSelectedCardOption("opus-doc")}
          />
          <PdfCardOption
            selected={selectedCardOption === "pdf"}
            onClick={() => setSelectedCardOption("pdf")}
          />
        </div>
        <div className="flex items-center justify-between border-t border-input p-4">
          <Text type="P3" className="text-muted-foreground">
            You can change this later from Resource Settings
          </Text>
          <div className="flex flex-1 items-center justify-end gap-2">
            <Button size="sm" variant="outline" onClick={props.onClose}>
              <Text type="P3" fontWeight="SemiBold">
                Cancel
              </Text>
            </Button>
            <Button
              size="sm"
              onClick={handleOnSave}
              disabled={fileListLoading || createTrainingResourceLoading}
            >
              <Text type="P3" fontWeight="SemiBold">
                {saveButtonText}
              </Text>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

type OpusDocFeature = {
  icon: React.ComponentType<IconProps>;
  text: string;
};

const OPUS_DOC_FEATURE_LIST: OpusDocFeature[] = [
  {
    icon: Phone2Icon,
    text: "Mobile-friendly format",
  },
  {
    icon: PenOutlineIcon,
    text: "Editable for quick changes",
  },
  {
    icon: LanguageIcon,
    text: "Easily translatable into multiple languages",
  },
];

interface CardProps {
  selected: boolean;
  onClick: (option: CardOption) => void;
}

const OpusDocCardOption: React.FC<CardProps> = ({ selected, onClick }) => {
  return (
    <div
      onClick={() => onClick("opus-doc")}
      className={cn(
        "flex h-[320px] max-w-[320px] flex-1 flex-col items-center justify-center gap-[16px] rounded-3xl border-2 border-input bg-background p-3 transition-all shadow-sm cursor-pointer",
        !selected && "hover:bg-gray-1",
        selected && "border-purple-9 bg-purple-1",
      )}
    >
      <div className="flex items-center justify-center self-stretch">
        <OpusDocumentIllustration
          accentClassName="fill-gray-2"
          className="size-[80px]"
        />
      </div>
      <Text type="P2" fontWeight="Medium">
        Convert into Opus Document
      </Text>
      <div className="flex flex-col gap-[4px] self-stretch">
        {OPUS_DOC_FEATURE_LIST.map((feature) => (
          <div
            key={feature.text}
            className="flex items-center gap-[8px] self-stretch"
          >
            <div className="flex size-8 items-center justify-center rounded-full bg-purple-alpha-1">
              <feature.icon className="size-4 text-purple-9" />
            </div>
            <Text type="P3" className="text-muted-foreground">
              {feature.text}
            </Text>
          </div>
        ))}
      </div>
    </div>
  );
};

const PdfCardOption: React.FC<CardProps> = ({ selected, onClick }) => {
  return (
    <div
      onClick={() => onClick("pdf")}
      className={cn(
        "flex h-[320px] max-w-[320px] flex-1 flex-col items-center justify-center gap-[16px] rounded-3xl border-2 border-input bg-background p-3 transition-all shadow-sm cursor-pointer",
        !selected && "hover:bg-gray-1",
        selected && "border-blue-9 bg-blue-1",
      )}
    >
      <PdfDocumentIllustration className="size-[80px]" />
      <div className="flex flex-col items-center gap-3">
        <Text type="P2" fontWeight="Medium">
          Keep as original PDF
        </Text>
        <Text type="P3" className="text-center text-muted-foreground" multiline>
          PDF is non editable, not mobile friendly and not translatable.
        </Text>
      </div>
    </div>
  );
};

gql`
  mutation ConvertPdfToOpusDocDialog_CreateTrainingResource(
    $input: CreateTrainingResourceInput!
  ) {
    createTrainingResourceV2(input: $input) {
      success
      trainingResource {
        ...UploadedTrainingResource
      }
    }
  }
`;

export default ConvertPdfToOpusDocDialog;
